










































































































































































import {
	computed,
	defineComponent,
	PropType,
	Ref,
	ref,
	watch,
} from '@vue/composition-api';
import { useFocusFirstInvalidField } from '@front-app/hooks/useFocusFirstInvalidField';
import {
	isFloat,
	maxParagraphs,
	required,
	requiredIfBoth,
	maxLenHtml,
} from '@front-app/components/form/validation/validators';
import { useTiptapEditor } from '@front-app/components/form/tiptap/useTiptapEditor';
import { v } from '@front-app/components/form/validation/useValidation';
import { ButtonPalettes } from '@front-app/components/buttons/palettes';
import { Size } from '@front-app/components/constants';
import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import { EMPTY_COUNTRY, EMPTY_REGION } from '@front-app/components/inputs/CountryPickerData';
import { CountryCode, RegionCode } from '@front-app/services/SharedModels';
import { UpsertProgramRequest } from '@front-app/api-generated/learning-management-api';
import { htmlToText } from '@front-app/util/content';
import { syncedRef } from '@front-app/util/reactivity';
import TrForm from '@front-app/components/form/Form.vue';
import FormSection from '@front-app/components/form/FormSection.vue';
import FormInput from '@front-app/components/form/FormInput.vue';
import FormLabel from '@front-app/components/form/FormLabel.vue';
import FormError from '@front-app/components/form/FormError.vue';
import EditorMenu from '@front-app/components/form/tiptap/EditorMenu.vue';
import EditorContent from '@front-app/components/form/tiptap/EditorContent.vue';
import EditorContainer from '@front-app/components/form/tiptap/EditorContainer.vue';
import HelpTooltipBlock from '@front-app/components/dropdowns/HelpTooltipBlock.vue';
import CtaButton from '@front-app/components/buttons/CtaButton.vue';
import CountryPicker from '@front-app/components/inputs/CountryPicker.vue';
import RegionPicker from '@front-app/components/inputs/RegionPicker.vue';
import RichTextSection from '@front-app/components/form/RichTextSection.vue';
import { LearningProgram } from '../../Model';
import ShowAccreditedBadgeCheckbox from '../inputs/ShowAccreditedBadgeCheckbox.vue';
import SendCertificateCheckbox from '../inputs/SendCertificateCheckbox.vue';
import { getLearningProgramsStore } from '../../store/LearningProgramsStore';
import SelectSpeakers from '../inputs/SelectSpeakers.vue';
import { useSelectSpeakers } from '../inputs/selectSpeakers';
import CertificateLanguagePicker from '../inputs/CertificateLanguagePicker.vue';

const CERTIFICATE_TEXT_MAX_CHARS = 650;
const CERTIFICATE_TEXT_MAX_PARAS = 4;

export default defineComponent({
	components: {
		CtaButton,
		TrForm,
		FormSection,
		FormInput,
		FormLabel,
		FormError,
		EditorMenu,
		EditorContent,
		EditorContainer,
		HelpTooltipBlock,
		CountryPicker,
		RegionPicker,
		ShowAccreditedBadgeCheckbox,
		SendCertificateCheckbox,
		SelectSpeakers,
		RichTextSection,
		CertificateLanguagePicker,
	},
	props: {
		program: {
			type: Object as PropType<LearningProgram>,
			required: true,
		},
		groupId: {
			type: String,
			required: true,
		},
	},
	setup(props, ctx) {
		const programsStore = getLearningProgramsStore();

		// Fields
		const name = syncedRef(() => props.program.name).ref;
		const identifier = syncedRef(() => (props.program.identifier ?? '')).ref;
		const certificateText = syncedRef(() => (props.program.certificateText ?? '')).ref;
		const sendCertificate = syncedRef(() => props.program.sendCertificate).ref;
		const certificateLocale = syncedRef(() => props.program.certificateLocale).ref;

		const el = ref(null as null | Element);
		const { editor } = useTiptapEditor(el);

		watch(() => props.program.description, (content) => {
			editor.chain().setContent(content).run();
		}, { immediate: true });

		const {
			speakerIds,
			removeSpeaker,
			selectSpeaker,
		} = useSelectSpeakers(() => props.program.speakers);

		const floatValidator = isFloat(2);
		const { ref: credits } = syncedRef(() => props.program.credits);
		const setCredits = (inputVal: string) => {
			credits.value = floatValidator.validator(inputVal) ? parseFloat(inputVal) : (inputVal as unknown as number);
		};

		const country: Ref<CountryCode> = ref(EMPTY_COUNTRY);
		const region: Ref<RegionCode> = ref(EMPTY_REGION);
		watch(() => props.program.location, (curr) => {
			country.value = (curr?.country as CountryCode) || EMPTY_COUNTRY;
			region.value = (curr?.provStateRegion as RegionCode) || EMPTY_REGION;
		}, { immediate: true });

		const { ref: showAccreditedBadge } = syncedRef(() => Boolean(props.program.showAccreditedBadge));

		// Validation
		const {
			focusFirstInvalid,
		} = useFocusFirstInvalidField({ containerSelector: '.edit-program-form' });
		const validation = v({
			name: {
				value: name,
				rules: [required()],
			},
			credits: {
				value: credits,
				rules: [floatValidator],
			},
			region: {
				value: region,
				rules: [requiredIfBoth(() => country.value)],
			},
			certificateTextParas: {
				value: certificateText,
				rules: [maxParagraphs(CERTIFICATE_TEXT_MAX_PARAS)],
			},
			certificateTextChars: {
				value: certificateText,
				rules: [maxLenHtml(CERTIFICATE_TEXT_MAX_CHARS)],
			},
		});

		const certificateTextErrorText = computed(() => {
			if (!validation.fields.certificateTextParas.isValid) {
				return `Certificate text must be ${CERTIFICATE_TEXT_MAX_PARAS} or fewer paragraphs.`;
			}
			if (!validation.fields.certificateTextChars.isValid) {
				const actualChars = htmlToText(certificateText.value).length;
				return `Certificate is too long: ${actualChars}/${CERTIFICATE_TEXT_MAX_CHARS} characters.`;
			}
			return '';
		});

		const setCertificateText = (newVal: string) => {
			certificateText.value = newVal;
			validation.fields.certificateTextParas.validate();
			validation.fields.certificateTextChars.validate();
		};

		// Submission
		const {
			track,
			isError,
			isLoading,
		} = useAsyncStatus();
		const updateProgram = async () => {
			validation.validate();

			if (!validation.isValid.value) {
				focusFirstInvalid();
				return;
			}
			const payload: UpsertProgramRequest = {
				id: props.program.id,
				name: name.value,
				identifier: identifier.value,
				description: editor.getHTML(),
				speakers: speakerIds.value,
				credits: credits.value,
				showAccreditedBadge: showAccreditedBadge.value,
				sendCertificate: sendCertificate.value,
				certificateText: certificateText.value,
				certificateLocale: certificateLocale.value,
				location: {
					country: sendCertificate.value ? country.value : EMPTY_COUNTRY,
					provStateRegion: sendCertificate.value ? region.value : EMPTY_REGION,
					street1: '',
					street2: '',
					city: '',
					postalCode: '',
				},
			};

			await track(programsStore.updateProgram(props.groupId, props.program.id, payload));

			ctx.emit('updated');
		};

		return {
			// Fields
			name,
			identifier,
			speakerIds,
			credits,
			setCredits,
			country,
			region,
			showAccreditedBadge,
			sendCertificate,
			certificateLocale,
			certificateText,
			setCertificateText,
			certificateTextErrorText,
			editor,
			// Updates
			validation,
			updateProgram,
			isError,
			isLoading,
			selectSpeaker,
			removeSpeaker,
			// Util
			submitButtonPalette: ButtonPalettes.PRIMARY,
			submitButtonSize: Size.LARGE,
		};
	},
});
